import React from 'react'
import { Ellipsis } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import DmcDataView from './DmcDataView'
import { camelCaseToReadable, printValueOrBooleanReadable, isNumber } from '../../util/StringUtil'
import { MrnLink, OsidLink, PersonLink, RelationshipLink, TreeLink, UserLink } from '../app/Links'
import { timestampToDateString } from '../../util/DateUtil'

const STANDARD_KEYS = {
  PERSON_ID: 'personId',
  PARENT1_ID: 'parent1Id',
  PARENT2_ID: 'parent2Id',
  PARENT_CHILD_ID: 'parent_childId',
  CHILD_ID: 'childId',
  COUPLE_ID: 'coupleId',
  ASSOCIATION_ID: 'associationId',
  SPOUSE1_ID: 'spouse1Id',
  SPOUSE2_ID: 'spouse2Id',
  PERSON1_ID: 'person1Id',
  PERSON2_ID: 'person2Id',
  CONTRIBUTOR_ID: 'contributorId',
  TREE_ID: 'treeId',
  MODIFIED: 'modified',
  OSID: 'osid',
  PLACE: 'place',
  DATE: 'date',
  NAME: 'name',
  MRN: 'mrn',
}

const relationships = {
  [STANDARD_KEYS.COUPLE_ID]: 'couple',
  [STANDARD_KEYS.ASSOCIATION_ID]: 'association',
  [STANDARD_KEYS.PARENT_CHILD_ID]: 'parentchild',
}

export default function ObjectView({ id, object, metadata, expanderDetails, fieldsToOmit, noCamelCase, noBorder }) {
  const { t } = useTranslation()
  const formattedExpanderDetails = { ...expanderDetails }
  if (object == null) {
    return <DmcDataView id={id} data={null} />
  }

  const getStandardKey = (key) => {
    return Object.values(STANDARD_KEYS).find((standardKey) => key.toLowerCase().includes(standardKey.toLowerCase()))
  }

  const formatObject = (objectToFormat) => {
    if (!objectToFormat) {
      return null
    }
    return Object.keys(objectToFormat)
      .filter((value) => (value && fieldsToOmit ? !fieldsToOmit.includes(value) : true))
      .flatMap((key) => {
        if (objectToFormat[key] == null) {
          return []
        }
        const dataRow = {}
        dataRow.label = t(`objectView.label.${key}`, noCamelCase ? key : camelCaseToReadable(key))
        dataRow.key = key

        if (typeof objectToFormat[key] === 'object') {
          dataRow.detail = <Ellipsis>{JSON.stringify(objectToFormat[key], null, 4)}</Ellipsis>
        } else {
          dataRow.detail = printValueOrBooleanReadable(`${objectToFormat[key]}`)
          if (metadata?.copyValueFields?.includes(key)) {
            dataRow.copyValue = dataRow.detail
          }
        }

        switch (getStandardKey(key)) {
          case STANDARD_KEYS.PERSON_ID:
          case STANDARD_KEYS.PARENT1_ID:
          case STANDARD_KEYS.PARENT2_ID:
          case STANDARD_KEYS.CHILD_ID:
          case STANDARD_KEYS.SPOUSE1_ID:
          case STANDARD_KEYS.SPOUSE2_ID:
          case STANDARD_KEYS.PERSON1_ID:
          case STANDARD_KEYS.PERSON2_ID:
            {
              const personId = objectToFormat[key]
              const name = metadata?.persons ? metadata.persons[personId]?.name || null : null
              const showId = !!name
              dataRow.detail = <PersonLink personId={personId} personName={name} showId={showId} newTab />
            }
            break
          case STANDARD_KEYS.CONTRIBUTOR_ID:
            dataRow.detail = <UserLink userId={objectToFormat[key]} newTab />
            break
          case STANDARD_KEYS.TREE_ID:
            dataRow.detail = <TreeLink treeId={objectToFormat[key]} />
            break
          case STANDARD_KEYS.PARENT_CHILD_ID:
          case STANDARD_KEYS.COUPLE_ID:
          case STANDARD_KEYS.ASSOCIATION_ID:
            dataRow.detail = (
              <RelationshipLink relationshipId={objectToFormat[key]} relationshipType={relationships[key]} newTab />
            )
            break
          case STANDARD_KEYS.OSID:
            dataRow.detail = <OsidLink osid={objectToFormat[key]} />
            break
          case STANDARD_KEYS.MODIFIED:
            if (isNumber(objectToFormat[key])) {
              dataRow.detail = timestampToDateString({ timestamp: objectToFormat[key] })
            }
            break
          case STANDARD_KEYS.PLACE:
            dataRow.detail =
              objectToFormat[key].names?.[0]?.value || objectToFormat[key].original || objectToFormat[key]
            break
          case STANDARD_KEYS.DATE:
            dataRow.detail =
              objectToFormat[key].normalized?.[0]?.value || objectToFormat[key].original || objectToFormat[key]
            break
          case STANDARD_KEYS.NAME:
            dataRow.detail = objectToFormat[key][0]?.fullText || objectToFormat[key]
            break
          case STANDARD_KEYS.MRN:
            {
              const mrn = objectToFormat[key]
              const name = metadata?.leaders?.[key]?.name
              dataRow.label = metadata?.leaders?.[key]?.label || camelCaseToReadable(key)
              dataRow.detail = <MrnLink mrn={mrn} mrnText={name} newTab />
              dataRow.copyValue = name
            }
            break
          default:
            break
        }
        return [dataRow]
      })
  }

  if (expanderDetails) {
    formattedExpanderDetails.dataList = expanderDetails.dataList?.map((expandedObject) => formatObject(expandedObject))
  }

  return (
    <DmcDataView id={id} data={formatObject(object)} expanderDetails={formattedExpanderDetails} noBorder={noBorder} />
  )
}
